import type { AppProps } from 'next/app';
import '@/assets/css/main.css';
import '@/assets/css/custom-plugins.css';
import '@/assets/css/rich-text-editor.css';
import 'react-toastify/dist/ReactToastify.css';
import { ModalProvider } from '@/components/ui/modal/modal.context';
import ManagedModal from '@/components/ui/modal/managed-modal';
import ManagedDrawer from '@/components/ui/drawer/managed-drawer';
import DefaultSeo from '@/components/seo/default-seo';
import { SearchProvider } from '@/components/ui/search/search.context';
import PrivateRoute from '@/lib/private-route';
import { CartProvider } from '@/store/quick-cart/cart.context';
import CartBackgroundPriceUpdater from '@/components/cart/cart-background-price-updater';
import CartPbmComboGuard from '@/components/cart/cart-pbm-combo-verifier';
import { CartSyncBootstrap } from '@/components/cart/cart-sync-bootstrap';
import SocialLogin from '@/components/auth/social-login';
import { NextPageWithLayout } from '@/types';
import QueryProvider from '@/framework/client/query-provider';
import { getDirection } from '@/lib/constants';
import { useAppLocale } from '@/lib/hooks/use-app-locale';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
const ToastContainer = dynamic(
  () => import('react-toastify').then((module) => module.ToastContainer),
  { ssr: false },
);
const RouletteWidget = dynamic(
  () => import('@/components/roulette/roulette-widget'),
  { ssr: false },
);
const PromotionalSticker = dynamic(
  () => import('@/components/promotions/promotional-sticker'),
  { ssr: false },
);
import ApiHealthGate from '@/components/infra-maintenance/api-health-gate';
import Maintenance from '@/components/maintenance/layout';
import { NotificationProvider } from '@/context/notify-content';
import ubuntu from '@/lib/fonts/ubuntu';
import { gtmPageView } from '@/lib/gtm';
import { useEffect } from 'react';
import NativeDeeplinkHandler from '@/components/common/native-deeplink-handler';
import NetcoreHandler from '@/components/common/netcore-handler';
import OldWebViewDetector from '@/components/common/old-webview-detector';
import { datadogInit } from '@/lib/datadog';
import { grafanaFaroInit } from '@/lib/grafana-faro';
import { isGrafanaFaroEnabled } from '@/lib/grafana-faro-enabled';
import { retireLegacyPwa } from '@/lib/register-service-worker';
import { captureCheckoutAttribution } from '@/lib/checkout-attribution';

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

export function OldAppDetectorTest() {


  return null;
}

function CustomApp({ Component, pageProps }: AppPropsWithLayout) {
  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? ((page) => page);
  const authenticationRequired = Component.authenticationRequired ?? false;
  const locale = useAppLocale();
  const dir = getDirection(locale);
  const router = useRouter();

  useEffect(() => {
    datadogInit();
    if (isGrafanaFaroEnabled) {
      void grafanaFaroInit();
    }
    retireLegacyPwa();
    captureCheckoutAttribution(router.asPath);
    const handleRouteChange = (url: string) => {
      gtmPageView(url);
      captureCheckoutAttribution(url);
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => router.events.off('routeChangeComplete', handleRouteChange);
  }, [router]);

  return (
    <div dir={dir} className={ubuntu.variable}>
      <QueryProvider pageProps={pageProps}>
        <ApiHealthGate>
          <SearchProvider>
            <ModalProvider>
              <CartProvider>
                <>
                  <CartSyncBootstrap />
                  <CartBackgroundPriceUpdater />
                  <CartPbmComboGuard />
                  <DefaultSeo />
                  <Maintenance>
                    <NotificationProvider>
                      {authenticationRequired ? (
                        <PrivateRoute>
                          {getLayout(<Component {...pageProps} />)}
                        </PrivateRoute>
                      ) : (
                        getLayout(<Component {...pageProps} />)
                      )}
                    </NotificationProvider>
                  </Maintenance>
                  <ManagedModal />
                  <ManagedDrawer />
                  <RouletteWidget />
                  <PromotionalSticker />
                  <ToastContainer autoClose={5000} theme="colored" />
                  <SocialLogin />
                  <NativeDeeplinkHandler />
                  <NetcoreHandler />
                  <OldWebViewDetector />
                </>
              </CartProvider>
            </ModalProvider>
          </SearchProvider>
        </ApiHealthGate>
      </QueryProvider>
    </div>
  );
}

export default CustomApp;
